import type { Message } from './types';

export type ChatTurnDebugToolExecution = {
  name: string;
  args: Record<string, unknown>;
  ok: boolean;
  summary: string;
  result: string;
  targetTitle?: string;
};

export type ChatTurnDebugRound = {
  kind: 'completion' | 'stream';
  round: number;
  toolNames: string[];
  response: {
    model: string;
    content: string | null;
    toolCalls: Array<{
      id: string;
      type: 'function';
      function: { name: string; arguments: string };
    }>;
    finishReason: string | null;
    usage: Record<string, number | undefined>;
  };
  toolExecutions: ChatTurnDebugToolExecution[];
};

export type CompletionMessage =
  | { role: 'system'; content: string }
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string | null; tool_calls?: ChatTurnDebugRound['response']['toolCalls'] }
  | { role: 'tool'; tool_call_id: string; content: string };

export type ChatTurnDebugTrace = {
  version: 1;
  source: 'trace' | 'reconstructed';
  assistantMessageId?: string;
  model: string;
  userText: string;
  contextTokens: number;
  initialMessages: CompletionMessage[];
  tools: Array<{ name: string; description: string }>;
  rounds: ChatTurnDebugRound[];
  finalMessages: CompletionMessage[];
  usage: Record<string, number | undefined>;
  assistantText: string;
  createdAt: string;
};

export type ChatPromptPreview = {
  previewText: string;
  contextTokens: number;
  model: string;
  messages: CompletionMessage[];
  tools: Array<{ name: string; description: string }>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

export function isChatTurnDebugTrace(value: unknown): value is ChatTurnDebugTrace {
  return isRecord(value) && value.version === 1 && typeof value.userText === 'string';
}

function normalizeTrace(trace: ChatTurnDebugTrace): ChatTurnDebugTrace {
  return { ...trace, source: trace.source ?? 'trace' };
}

type ToolCall = ChatTurnDebugRound['response']['toolCalls'][number];

function asString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return '';
  }
  return JSON.stringify(value);
}

function messageToCompletion(message: Message): CompletionMessage | null {
  const content = message.content;
  if (message.role === 'user') {
    return { role: 'user', content: asString(content.text) };
  }
  if (message.role === 'assistant') {
    const toolCalls = Array.isArray(content.toolCalls) ? (content.toolCalls as ToolCall[]) : undefined;
    return {
      role: 'assistant',
      content: typeof content.text === 'string' ? content.text : null,
      ...(toolCalls && toolCalls.length > 0 ? { tool_calls: toolCalls } : {}),
    };
  }
  if (message.role === 'tool') {
    return {
      role: 'tool',
      tool_call_id: asString(content.toolCallId),
      content: asString(content.result),
    };
  }
  return null;
}

function newReconstructedTurn(userText: string, initialMessages: CompletionMessage[]): ChatTurnDebugTrace {
  return {
    version: 1,
    source: 'reconstructed',
    model: '',
    userText,
    contextTokens: 0,
    initialMessages,
    tools: [],
    rounds: [],
    finalMessages: [],
    usage: {},
    assistantText: '',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Build debug turns for the pane. Prefers the rich server-side trace stored on an
 * assistant message; otherwise reconstructs a turn from the stored message history
 * (user → assistant tool calls → tool results → final reply) so any conversation,
 * old or new, shows useful diagnostics.
 */
export function buildDebugTurns(messages: Message[]): ChatTurnDebugTrace[] {
  const turns: ChatTurnDebugTrace[] = [];
  const running: CompletionMessage[] = [];
  let current: ChatTurnDebugTrace | null = null;
  let currentRound: ChatTurnDebugRound | null = null;

  const finalize = () => {
    if (current) {
      current.finalMessages = running.map((message) => ({ ...message }));
      turns.push(current);
      current = null;
      currentRound = null;
    }
  };

  for (const message of messages) {
    const completion = messageToCompletion(message);

    if (message.role === 'user') {
      finalize();
      if (completion) {
        running.push(completion);
      }
      current = newReconstructedTurn(asString(message.content.text), running.map((m) => ({ ...m })));
      continue;
    }

    if (message.role === 'assistant') {
      const stored = message.content.debugTrace;
      const toolCalls = Array.isArray(message.content.toolCalls)
        ? (message.content.toolCalls as ToolCall[])
        : [];

      if (toolCalls.length > 0) {
        if (!current) {
          current = newReconstructedTurn('(unknown)', running.map((m) => ({ ...m })));
        }
        const round: ChatTurnDebugRound = {
          kind: 'completion',
          round: current.rounds.length,
          toolNames: toolCalls.map((call) => call.function.name),
          response: {
            model: message.model ?? '',
            content: typeof message.content.text === 'string' ? message.content.text : null,
            toolCalls,
            finishReason: 'tool_calls',
            usage: {},
          },
          toolExecutions: [],
        };
        current.rounds.push(round);
        currentRound = round;
      } else {
        // Final assistant reply for this turn.
        if (isChatTurnDebugTrace(stored)) {
          const trace = normalizeTrace(stored);
          trace.assistantMessageId = message.id;
          current = trace;
        } else if (current) {
          current.assistantText = asString(message.content.text);
          current.model = message.model ?? current.model;
          current.contextTokens = message.contextTokens ?? current.contextTokens;
          current.assistantMessageId = message.id;
          current.usage = {
            prompt_tokens: message.promptTokens ?? undefined,
            completion_tokens: message.completionTokens ?? undefined,
          };
          current.createdAt = message.createdAt;
        }
        currentRound = null;
      }

      if (completion) {
        running.push(completion);
      }
      continue;
    }

    if (message.role === 'tool') {
      if (currentRound) {
        currentRound.toolExecutions.push({
          name: message.toolName ?? 'tool',
          args: isRecord(message.content.args) ? message.content.args : {},
          ok: message.content.ok !== false,
          summary: asString(message.content.summary),
          result: asString(message.content.result),
          targetTitle:
            typeof message.content.targetTitle === 'string' ? message.content.targetTitle : undefined,
        });
      }
      if (completion) {
        running.push(completion);
      }
    }
  }

  finalize();
  return turns;
}

/** @deprecated Use buildDebugTurns, which also reconstructs from message history. */
export function extractDebugTurns(messages: Message[]): ChatTurnDebugTrace[] {
  return buildDebugTurns(messages);
}

function formatCompletionMessage(message: CompletionMessage, index: number): string {
  const header = `--- message ${index + 1} [${message.role}] ---`;
  switch (message.role) {
    case 'system':
    case 'user':
      return `${header}\n${message.content}`;
    case 'assistant': {
      const parts = [header];
      if (message.content) {
        parts.push(message.content);
      }
      if (message.tool_calls && message.tool_calls.length > 0) {
        parts.push(`tool_calls:\n${JSON.stringify(message.tool_calls, null, 2)}`);
      }
      return parts.join('\n');
    }
    case 'tool':
      return `${header}\n(tool_call_id: ${message.tool_call_id})\n${message.content}`;
    default:
      return header;
  }
}

function formatMessagesBlock(title: string, messages: CompletionMessage[]): string {
  if (messages.length === 0) {
    return `${title}\n(empty)`;
  }
  return `${title}\n${messages.map((message, index) => formatCompletionMessage(message, index)).join('\n\n')}`;
}

function formatUsage(usage: Record<string, number | undefined>): string {
  const parts: string[] = [];
  if (usage.prompt_tokens != null) {
    parts.push(`prompt: ${usage.prompt_tokens}`);
  }
  if (usage.completion_tokens != null) {
    parts.push(`completion: ${usage.completion_tokens}`);
  }
  if (usage.total_tokens != null) {
    parts.push(`total: ${usage.total_tokens}`);
  }
  if (usage.cost != null) {
    parts.push(`cost: ${usage.cost}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'n/a';
}

export function formatAllDebugTurnsForCopy(turns: ChatTurnDebugTrace[]): string {
  if (turns.length === 0) {
    return '# Couplespace chat debug log\n\n(no turns recorded)';
  }
  return turns.map((trace, index) => {
    const body = formatTurnDebugForCopy(trace);
    if (turns.length === 1) {
      return body;
    }
    return `# Turn ${index + 1} of ${turns.length}\n\n${body}`;
  }).join('\n\n---\n\n');
}

export function formatTurnDebugForCopy(trace: ChatTurnDebugTrace): string {
  const lines: string[] = [
    '# Couplespace chat turn debug',
    '',
    `source: ${trace.source}${trace.source === 'reconstructed' ? ' (system prompt not captured — use "Load current prompt")' : ''}`,
    `created: ${trace.createdAt}`,
    `model: ${trace.model}`,
    `context tokens (est.): ${trace.contextTokens}`,
    `usage: ${formatUsage(trace.usage)}`,
    '',
    '## User message',
    trace.userText,
    '',
    '## Tools offered',
    trace.tools.length > 0
      ? trace.tools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')
      : '(none)',
    '',
    formatMessagesBlock('## Initial prompt messages', trace.initialMessages),
  ];

  for (const round of trace.rounds) {
    lines.push(
      '',
      `## Round ${round.round} (${round.kind})`,
      `tools: ${round.toolNames.length > 0 ? round.toolNames.join(', ') : '(none)'}`,
      '',
      '### Model response',
      `model: ${round.response.model || trace.model}`,
      `finish: ${round.response.finishReason ?? 'n/a'}`,
      `usage: ${formatUsage(round.response.usage)}`,
      round.response.content ? `\n${round.response.content}` : '',
    );
    if (round.response.toolCalls.length > 0) {
      lines.push('', 'tool_calls:', JSON.stringify(round.response.toolCalls, null, 2));
    }
    if (round.toolExecutions.length > 0) {
      lines.push('', '### Tool executions');
      for (const exec of round.toolExecutions) {
        lines.push(
          '',
          `- ${exec.name}${exec.targetTitle ? ` ("${exec.targetTitle}")` : ''}`,
          `  ok: ${exec.ok}`,
          `  summary: ${exec.summary}`,
          `  args: ${JSON.stringify(exec.args)}`,
          `  result: ${exec.result}`,
        );
      }
    }
  }

  lines.push(
    '',
    formatMessagesBlock('## Final messages sent to model', trace.finalMessages),
    '',
    '## Assistant reply',
    trace.assistantText || '(empty)',
  );

  return lines.filter((line) => line !== undefined).join('\n');
}

export function turnDebugPreview(trace: ChatTurnDebugTrace): string {
  const text = trace.userText.trim().replace(/\s+/g, ' ');
  const preview = text.length > 72 ? `${text.slice(0, 71)}…` : text;
  const rounds = trace.rounds.length;
  const src = trace.source === 'reconstructed' ? 'reconstructed' : 'captured';
  return `"${preview}" · ${rounds} round${rounds === 1 ? '' : 's'} · ${src}`;
}

export function formatPromptPreviewForCopy(preview: ChatPromptPreview): string {
  return [
    '# Couplespace current prompt preview',
    '',
    `model: ${preview.model}`,
    `context tokens (est.): ${preview.contextTokens}`,
    preview.previewText ? `preview user text: ${preview.previewText}` : 'preview user text: (empty)',
    '',
    '## Tools offered',
    preview.tools.length > 0
      ? preview.tools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')
      : '(none)',
    '',
    formatMessagesBlock('## Prompt messages', preview.messages),
  ].join('\n');
}
